import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Currency, Fee } from '@hero/schema';
import { computeAmountPreview, getTaxRate } from '@hero/core';
import { usePublishOrderStore } from '@hero/hooks-shared';
import { ArrowRightIcon, ChevronDownIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Price } from '../../../../app/Price';

const DEFAULT_VAT_RATE = getTaxRate('saudi_vat');

/**
 * Inline, expandable "Total" row for the side-panel order summary.
 * Replaces the standalone card we used to render under the summary —
 * one collapsed line shows the grand total, expanding shows the
 * per-destination breakdown + subtotal + tax + total.
 *
 * Source of truth:
 *   - resolver-provided per-leg price from `state.pricePreview.routes[i]`,
 *   - per-destination `priceOverrides[i]` taking precedence (the user
 *     locked a one-time price for that leg via the chip's edit drawer),
 *   - **multiplied by the destination's truck count** from
 *     `destinationVehicleQuantities[i]` — the resolver returns the
 *     per-truck rate, so 2 trucks on a SAR 1,000 leg cost SAR 2,000
 *     plus tax. Mirrors how the backend amounts the order on save.
 *
 * VAT is always applied — see `OrderPriceEditDrawer` (publish-draft
 * mode) and `.cursorrules` (`Amount math`); there is no per-leg
 * opt-out so this card never branches on `taxable`.
 */
export function PriceSummaryCard() {
  const { t } = useTranslation('m-order');
  const origin = usePublishOrderStore(s => s.origin);
  const destinations = usePublishOrderStore(s => s.destinations);
  const destinationVehicleQuantities = usePublishOrderStore(
    s => s.destinationVehicleQuantities,
  );
  const pricePreview = usePublishOrderStore(s => s.pricePreview);
  const priceOverrides = usePublishOrderStore(s => s.priceOverrides);
  const [isExpanded, setIsExpanded] = useState(false);

  const hasAnyDestination = destinations.some(d => d && d.lat !== 0);

  // Pickup label shown on the start of every breakdown row. Falls
  // back to a generic "Pickup" label when the user hasn't picked an
  // origin yet (the breakdown still renders so the chevron can be
  // expanded — the destination column will tell them what's still
  // missing).
  const fromLabel =
    origin?.city ||
    origin?.name ||
    t('wizard.price_summary.pickup_fallback', { defaultValue: 'Pickup' });

  // Tax-aware totals across every destination. Each destination
  // contributes whichever price applies (override → resolver →
  // zero), wrapped through `computeAmountPreview` so the math
  // matches what the backend will commit on save.
  const breakdown = useMemo(() => {
    const currency: Currency = pricePreview?.currency ?? 'SAR';

    const rows = destinations.map((dest, idx) => {
      const override = priceOverrides[idx];
      const route = pricePreview?.routes?.[idx];
      const perTruckSubtotal =
        override?.itemsOriginalSubtotal ?? route?.amount?.subtotal ?? 0;
      const trucks = destinationVehicleQuantities[idx] ?? 1;
      // Each leg's preview is computed standalone so we can sum
      // without re-creating the amount math here. We feed
      // `itemsOriginalSubtotal × trucks` so the resulting tax /
      // total reflect the full leg cost the backend will ultimately
      // amount. VAT is always applied — there's no opt-out per leg.
      const legCurrency = override?.currency ?? route?.amount?.currency ?? currency;
      const legPreview = computeAmountPreview({
        currency: legCurrency,
        itemsOriginalSubtotal: perTruckSubtotal * trucks,
        fees: override?.fees
          ? {
              applied: override.fees.map(f => ({
                ...f,
                // Per-truck fees scale with truck count, same as the
                // load price.
                amount: (f.amount ?? 0) * trucks,
                calcType: 'add_to_subtotal' as const,
              })) as Fee[],
            }
          : undefined,
        tax: {
          applied: [{ id: 'saudi_vat', percentage: DEFAULT_VAT_RATE, amount: 0 } as never],
        },
      });
      return {
        index: idx,
        currency: legCurrency,
        // Per-leg "→ city". Falls back to a numbered placeholder
        // when this destination row is still empty (so the user can
        // expand the breakdown before every pin is set and still
        // make sense of which leg is which).
        toLabel:
          dest?.city ||
          dest?.name ||
          t('form.destination.title', {
            defaultValue: 'Destination {{index}}',
            index: idx + 1,
          }),
        perTruckSubtotal,
        trucks,
        subtotal: legPreview.subtotal,
        tax: legPreview.tax.total,
        total: legPreview.total,
        isOverride: !!override,
        pricingSource: route?.pricingSource,
      };
    });

    const subtotalSum = rows.reduce((sum, r) => sum + r.subtotal, 0);
    const taxSum = rows.reduce((sum, r) => sum + r.tax, 0);
    const totalSum = rows.reduce((sum, r) => sum + r.total, 0);
    return { currency, rows, subtotalSum, taxSum, totalSum };
  }, [destinations, destinationVehicleQuantities, pricePreview, priceOverrides]);

  if (!hasAnyDestination) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        aria-expanded={isExpanded}
        className={cn(
          'group flex w-full items-center justify-between gap-3 rounded-md',
          '-mx-1 px-1 py-1 transition-colors hover:bg-gray-50',
        )}>
        <span className="flex items-center gap-1.5 text-base font-semibold text-gray-900">
          {t('wizard.price_summary.total', { defaultValue: 'Total' })}
          <ChevronDownIcon
            className={cn(
              'size-4 text-gray-400 group-hover:text-gray-600 transition-transform',
              isExpanded && 'rotate-180',
            )}
          />
        </span>
        <Price
          amount={breakdown.totalSum}
          currency={breakdown.currency}
          size="xl"
          className="font-semibold text-gray-900"
        />
      </button>

      {isExpanded && (
        <div className="space-y-2 text-sm border-t pt-3 -mt-1">
          {breakdown.rows.map(row => (
            <div
              key={row.index}
              className="flex items-center justify-between gap-3 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
              <div className="min-w-0 flex-1">
                {/* "From → To" route label. The flex container picks up
                    the document direction from `<html dir>` so the
                    visual order auto-flips in RTL; the arrow itself
                    has to be rotated 180° in RTL because the icon
                    glyph isn't bidi-aware on its own. */}
                <div className="flex items-center gap-1.5 min-w-0 text-[13px] font-medium text-gray-900">
                  <span className="truncate">{fromLabel}</span>
                  <ArrowRightIcon className="size-3 text-gray-400 flex-shrink-0 rtl:rotate-180" />
                  <span className="truncate">{row.toLabel}</span>
                </div>
                <div className="text-[11px] text-gray-500 truncate">
                  {[
                    row.isOverride
                      ? t('wizard.price_source.one_time', { defaultValue: 'One-time price' })
                      : row.pricingSource
                        ? t(`wizard.price_source.${row.pricingSource}`, {
                            defaultValue: row.pricingSource,
                          })
                        : t('wizard.price_summary.awaiting', {
                            defaultValue: 'Awaiting price',
                          }),
                    // Show the per-truck × N hint only when the row
                    // actually has more than one truck — keeps the
                    // common "1 truck" case clean.
                    row.trucks > 1
                      ? t('wizard.price_summary.per_truck', {
                          defaultValue: '{{count}} trucks',
                          count: row.trucks,
                        })
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              </div>
              <Price
                amount={row.subtotal}
                currency={row.currency}
                size="sm"
                className="font-semibold"
              />
            </div>
          ))}

          <div className="pt-2 space-y-1.5">
            <div className="flex items-center justify-between text-[12px] text-gray-500">
              <span>{t('wizard.price_summary.subtotal', { defaultValue: 'Subtotal' })}</span>
              <Price amount={breakdown.subtotalSum} currency={breakdown.currency} size="xs" />
            </div>
            {breakdown.taxSum > 0 && (
              <div className="flex items-center justify-between text-[12px] text-gray-500">
                <span>
                  {t('wizard.price_summary.tax', {
                    defaultValue: 'Tax ({{percent}}%)',
                    percent: Math.round(DEFAULT_VAT_RATE * 100),
                  })}
                </span>
                <Price amount={breakdown.taxSum} currency={breakdown.currency} size="xs" />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
